import { z } from "zod";

export const NOTIFICATION_TABS = ["history", "templates", "registered", "policies", "campaigns"] as const;
export type NotificationTab = (typeof NOTIFICATION_TABS)[number];

export const NOTIFICATION_STATUSES = ["PENDING", "SCHEDULED", "PROCESSING", "RETRY", "SENT", "FAILED", "IN_APP_ONLY"] as const;

const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
const localDateTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

function validDate(value: string): boolean {
  if (!value) return true;
  const match = isoDatePattern.exec(value);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  const parsed = new Date(Date.UTC(year!, month! - 1, day));
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month! - 1 && parsed.getUTCDate() === day;
}

const optionalPositiveId = (label: string) => z.string().trim().refine(
  (value) => value === "" || (/^\d+$/.test(value) && Number.isSafeInteger(Number(value)) && Number(value) > 0),
  `${label}는 1 이상의 숫자로 입력해 주세요.`,
);

const positiveId = (label: string) => z.string().trim().refine(
  (value) => /^\d+$/.test(value) && Number.isSafeInteger(Number(value)) && Number(value) > 0,
  `${label}는 1 이상의 숫자로 입력해 주세요.`,
);

const scheduledAt = z.string().refine(
  (value) => localDateTimePattern.test(value) && Number.isFinite(Date.parse(`${value}:00+09:00`)),
  "예약일시를 선택해 주세요.",
).refine(
  (value) => !localDateTimePattern.test(value) || Date.parse(`${value}:00+09:00`) > Date.now(),
  "예약일시는 현재 이후로 선택해 주세요.",
);

export const notificationSearchSchema = z.object({
  deliveryStatus: z.union([z.literal(""), z.enum(NOTIFICATION_STATUSES)]),
  memberId: optionalPositiveId("회원 ID"),
  campaignId: optionalPositiveId("캠페인 ID"),
  from: z.string().refine(validDate, "유효한 시작일을 선택해 주세요."),
  to: z.string().refine(validDate, "유효한 종료일을 선택해 주세요."),
}).superRefine((values, context) => {
  if (values.from && values.to && values.from > values.to) {
    context.addIssue({ code: "custom", message: "종료일은 시작일보다 빠를 수 없습니다.", path: ["to"] });
  }
});

export const singleNotificationSchema = z.object({
  templateId: z.string().regex(/^$|^[1-9]\d*$/, "템플릿을 선택해 주세요.").optional(),
  memberId: positiveId("회원 ID"),
  notificationType: z.string().trim().min(1, "알림 유형을 입력해 주세요.").max(50, "알림 유형은 50자 이하로 입력해 주세요."),
  title: z.string().trim().min(1, "제목을 입력해 주세요.").max(500, "제목은 500자 이하로 입력해 주세요."),
  body: z.string().trim().min(1, "본문을 입력해 주세요.").max(4_000, "본문은 4,000자 이하로 입력해 주세요."),
  scheduledAt,
});

export const notificationTemplateSchema = z.object({
  templateCode: z.string().trim().min(1, "템플릿 코드를 입력해 주세요.").max(80, "템플릿 코드는 80자 이하로 입력해 주세요."),
  notificationType: z.string().trim().min(1, "알림 유형을 입력해 주세요.").max(50, "알림 유형은 50자 이하로 입력해 주세요."),
  deliveryChannel: z.string().trim().min(1, "발송 채널을 입력해 주세요.").max(20, "발송 채널은 20자 이하로 입력해 주세요."),
  titleTemplate: z.string().trim().max(500, "제목 템플릿은 500자 이하로 입력해 주세요."),
  bodyTemplate: z.string().trim().min(1, "본문 템플릿을 입력해 주세요.").max(4_000, "본문 템플릿은 4,000자 이하로 입력해 주세요."),
});

export const notificationPolicySchema = z.object({
  policyGroupCode: z.string().trim().min(1, "정책 그룹 코드를 입력해 주세요.").max(80, "정책 그룹 코드는 80자 이하로 입력해 주세요."),
  stepOrder: z.string().regex(/^\d+$/, "단계는 숫자로 입력해 주세요.").refine((value) => Number(value) >= 1 && Number(value) <= 20, "단계는 1~20으로 입력해 주세요."),
  eventType: z.string().trim().min(1, "이벤트 유형을 입력해 주세요.").max(80, "이벤트 유형은 80자 이하로 입력해 주세요."),
  deliveryChannel: z.string().trim().min(1, "발송 채널을 입력해 주세요.").max(20, "발송 채널은 20자 이하로 입력해 주세요."),
  templateId: positiveId("템플릿 ID"),
  maxAttemptCount: z.string().regex(/^\d+$/, "최대 시도 횟수는 숫자로 입력해 주세요.").refine((value) => Number(value) >= 1 && Number(value) <= 5, "최대 시도 횟수는 1~5로 입력해 주세요."),
  retryIntervalMinutes: z.string().regex(/^\d+$/, "재시도 간격은 숫자로 입력해 주세요.").refine((value) => Number(value) >= 0 && Number(value) <= 1_440, "재시도 간격은 0~1,440분으로 입력해 주세요."),
  fallbackOnFailure: z.boolean(),
});

export const notificationCampaignSchema = z.object({
  campaignName: z.string().trim().min(1, "캠페인명을 입력해 주세요.").max(200, "캠페인명은 200자 이하로 입력해 주세요."),
  notificationType: z.string().trim().min(1, "알림 유형을 입력해 주세요.").max(50, "알림 유형은 50자 이하로 입력해 주세요."),
  templateId: positiveId("템플릿 ID"),
  memberIds: z.string().trim().superRefine((value, context) => {
    const entries = value.split(",").map((item) => item.trim()).filter(Boolean);
    if (entries.length === 0) context.addIssue({ code: "custom", message: "회원 ID를 한 개 이상 입력해 주세요." });
    else if (entries.length > 1_000) context.addIssue({ code: "custom", message: "회원 ID는 최대 1,000개까지 입력할 수 있습니다." });
    else if (entries.some((item) => !/^\d+$/.test(item) || !Number.isSafeInteger(Number(item)) || Number(item) <= 0)) {
      context.addIssue({ code: "custom", message: "회원 ID는 쉼표로 구분한 양수만 입력해 주세요." });
    }
  }),
  scheduledAt,
});

export type NotificationSearchValues = z.infer<typeof notificationSearchSchema>;
export type SingleNotificationValues = z.input<typeof singleNotificationSchema>;
export type NotificationTemplateValues = z.infer<typeof notificationTemplateSchema>;
export type NotificationPolicyValues = z.infer<typeof notificationPolicySchema>;
export type NotificationCampaignValues = z.infer<typeof notificationCampaignSchema>;

export function parseNotificationTab(value: string | null): NotificationTab {
  return NOTIFICATION_TABS.includes(value as NotificationTab) ? value as NotificationTab : "history";
}

export function parseNotificationStatus(value: string | null): NotificationSearchValues["deliveryStatus"] {
  const parsed = z.enum(NOTIFICATION_STATUSES).safeParse(value);
  return parsed.success ? parsed.data : "";
}

export function parsePositiveId(value: string | null): string {
  if (!value || !/^\d+$/.test(value)) return "";
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? String(number) : "";
}

export function parseNotificationDate(value: string | null): string {
  return value && validDate(value) ? value : "";
}

export function toUtcLocalDateTime(kstLocalDateTime: string): string {
  return new Date(`${kstLocalDateTime}:00+09:00`).toISOString().slice(0, 19);
}

export function parseMemberIds(value: string): number[] {
  return [...new Set(value.split(",").map((item) => Number(item.trim())).filter((item) => Number.isSafeInteger(item) && item > 0))];
}
